import React, { useEffect, useState, useCallback, useMemo } from 'react';

const API = 'https://tm-audit-platform-production.up.railway.app';

// Mirrors FAILURE_REVIEW_STATUSES in backend/feedback.js and the CHECK in
// migration 0032. Duplicated by hand, exactly as STATUSES already is in
// FeedbackCenter.js — the frontend has no shared-constants channel with the
// backend, and inventing one for four strings is not worth it.
const REVIEW_STATUSES = ['new', 'reviewed', 'reprocessed', 'dismissed'];

const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
const fmtBytes = (n) => {
    if (n === null || n === undefined) return '—';
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
    return (n / (1024 * 1024)).toFixed(1) + ' MB';
};

const statusColor = (s) => s === 'new' ? 'var(--amber)' : s === 'reprocessed' ? 'var(--green)' : s === 'dismissed' ? 'var(--text-muted)' : 'var(--blue)';

// The failure taxonomy splits cleanly into "their file, their fix" and "ours to
// answer" (see backend/audit-failure-log.js). Colour by that split rather than by
// code: it is the only distinction that changes what the person triaging does.
const CUSTOMER_FAULT = ['encrypted_pdf', 'invalid_pdf', 'empty_pdf', 'upload_too_large'];
const rowIcon = (r) => {
    if (CUSTOMER_FAULT.includes(r.failure_code)) return { glyph: '·', color: 'var(--text-muted)', bg: 'var(--surface2)' };
    if (r.failure_code === 'roofing_unsupported' || r.failure_code === 'unsupported_document') return { glyph: '◷', color: 'var(--amber-bright)', bg: 'var(--amber-dim)' };
    if (r.failure_code === 'scanned_no_text' || r.failure_code === 'extraction_low_confidence') return { glyph: '⛭', color: 'var(--blue)', bg: 'var(--blue-dim)' };
    return { glyph: '!', color: 'var(--red)', bg: 'var(--red-dim)' };
};

// The 8-char reference the customer reads off their own screen. Derived here the
// same way backend/audit-failure-log.js derives it, because it is the only
// identifier a support conversation usually has.
const supportRef = (correlationId) => {
    if (!correlationId) return null;
    const bare = String(correlationId).replace(/^ing_/, '').replace(/-/g, '');
    return bare ? bare.slice(0, 8).toUpperCase() : null;
};

/*
 * FailedAudits — the manual-review queue.
 *
 * Several failure messages promise the customer that a human can review their
 * document ("our team can review this estimate manually", "just ask and we will
 * pick it up from here"). This is where that human does it. Before this view, the
 * promise had no address: the row existed and nobody was looking at it.
 *
 * A sub-view of the Feedback Center rather than an admin tab of its own — it is
 * the same job (a customer hit something; a person needs to respond) against a
 * different table.
 */
function FailedAudits({ session }) {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [unavailable, setUnavailable] = useState(false);
    const [filters, setFilters] = useState({ review_status: '', has_file: '' });
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);

    const getHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
    }), [session]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const qs = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
            const res = await fetch(`${API}/api/admin/audit-failures?${qs.toString()}`, { headers: getHeaders() });
            if (!res.ok) throw new Error(`Request failed (${res.status})`);
            const data = await res.json();
            setRows(data.failures || []);
            setSummary(data.summary || null);
            setUnavailable(!!data.storage_unavailable);
        } catch (e) {
            setError(e.message);
        }
        setLoading(false);
    }, [filters, getHeaders]);

    useEffect(() => { load(); }, [load]);

    const patchSelected = async (patch) => {
        if (!selected) return;
        setSaving(true);
        try {
            const res = await fetch(`${API}/api/admin/audit-failures/${selected.id}`, {
                method: 'PATCH', headers: getHeaders(), body: JSON.stringify(patch),
            });
            if (!res.ok) throw new Error(`Update failed (${res.status})`);
            const data = await res.json();
            // Write the server's canonical row back into both places so the UI can
            // never drift from the DB.
            setSelected(data.failure);
            setRows(prev => prev.map(r => r.id === data.failure.id ? { ...r, ...data.failure } : r));
        } catch (e) { setError(e.message); }
        setSaving(false);
    };

    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r => [r.file_name, r.failure_code, r.user_message, r.error_message,
            r.correlation_id, supportRef(r.correlation_id), r.detected_document_type]
            .some(v => (v || '').toLowerCase().includes(q)));
    }, [rows, search]);

    const cards = summary ? [
        { key: 'needs', label: 'Needs Review', value: summary.needs_review, desc: 'Nobody has looked yet', color: 'var(--amber)' },
        { key: 'promised', label: 'Review Offered', value: summary.manual_review_offered, desc: 'We told them a human would look', color: 'var(--red)' },
        { key: 'file', label: 'File Kept', value: summary.with_file, desc: 'Document available to re-run', color: 'var(--green)' },
        { key: 'total', label: 'Total', value: summary.total, desc: 'Failures captured', color: 'var(--blue)' },
    ] : [];

    return (
        <div className="feedback-center">
            <div className="feedback-header">
                <div className="feedback-header-text">
                    <h2 className="feedback-title">Failed Audits</h2>
                    <p className="feedback-subtitle">Uploads that never produced an audit. Where we promised a human would look, the file is here.</p>
                </div>
                <div className="feedback-header-actions">
                    <button className="feedback-hbtn" onClick={load}>↻ Refresh</button>
                </div>
            </div>

            {/* Migrations 0031/0032 not applied yet — say so plainly rather than
                showing an empty queue that looks like good news. */}
            {unavailable && (
                <div className="feedback-empty" style={{ borderColor: 'var(--amber)', color: 'var(--amber)' }}>
                    Failure capture is not live yet — migrations 0031 and 0032 have not been applied to this environment.
                    Audits are unaffected; failures are simply not being recorded.
                </div>
            )}

            <div className="feedback-cards">
                {cards.map(c => (
                    <div className="feedback-card" key={c.key}>
                        <div className="feedback-card-value" style={{ color: c.color }}>{c.value}</div>
                        <div className="feedback-card-label">{c.label}</div>
                        <div className="feedback-card-desc">{c.desc}</div>
                    </div>
                ))}
            </div>

            <div className="feedback-controls-wrap">
                <div className="feedback-controls">
                    <select className="feedback-control-select" value={filters.review_status}
                        onChange={e => setFilters(f => ({ ...f, review_status: e.target.value }))}>
                        <option value="">All Statuses</option>
                        {REVIEW_STATUSES.map(s => <option key={s} value={s}>{labelize(s)}</option>)}
                    </select>
                    <input className="feedback-search" type="text" placeholder="Search file, code, reference…"
                        value={search} onChange={e => setSearch(e.target.value)} />
                    <button className={`feedback-toggle ${filters.has_file === 'true' ? 'active' : ''}`}
                        onClick={() => setFilters(f => ({ ...f, has_file: f.has_file === 'true' ? '' : 'true' }))}>
                        Has File
                    </button>
                </div>
            </div>

            {error && <div className="feedback-error" style={{ margin: '8px 0' }}>{error}</div>}
            {loading && <div className="history-loading"><div className="spinner" /></div>}

            {!loading && visibleRows.length === 0 && !unavailable && (
                <div className="feedback-empty">
                    {rows.length === 0
                        ? `No failed audits${Object.values(filters).some(Boolean) ? ' for these filters' : ''}.`
                        : 'No failed audits match your search.'}
                </div>
            )}

            {!loading && visibleRows.length > 0 && (
                <div className="admin-table-wrap">
                    <table className="admin-table feedback-table">
                        <thead>
                            <tr>
                                <th>Failure</th>
                                <th>Reference</th>
                                <th>Status</th>
                                <th>Detected</th>
                                <th>File</th>
                                <th>Date</th>
                                <th aria-label="Open" />
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map(r => {
                                const icon = rowIcon(r);
                                return (
                                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(r)}>
                                        <td>
                                            <div className="feedback-cell">
                                                <span className="feedback-cell-icon" style={{ color: icon.color, background: icon.bg }}>{icon.glyph}</span>
                                                <div className="feedback-cell-main">
                                                    <div className="feedback-cell-title">
                                                        {labelize(r.failure_code) || 'Unknown Failure'}
                                                        {r.manual_review_offered && <span className="feedback-badge feedback-badge-blue">Review Offered</span>}
                                                        {r.document_hint && <span className="feedback-badge feedback-badge-amber">{labelize(r.document_hint)}</span>}
                                                    </div>
                                                    <div className="feedback-cell-sub">{r.file_name || '(no file name)'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)' }}>{supportRef(r.correlation_id) || '—'}</td>
                                        <td style={{ color: statusColor(r.review_status), fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{labelize(r.review_status)}</td>
                                        <td style={{ fontSize: 12 }}>{r.detected_document_type || '—'}</td>
                                        <td style={{ fontSize: 12 }}>
                                            {r.file_available
                                                ? <span style={{ color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700 }}>{fmtBytes(r.stored_size_bytes)}</span>
                                                : <span style={{ color: 'var(--text-muted)' }}>{r.storage_deleted_at ? 'Expired' : '—'}</span>}
                                        </td>
                                        <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDateShort(r.created_at)}</td>
                                        <td className="feedback-chevron">›</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <FailedAuditDetail
                    item={selected}
                    saving={saving}
                    session={session}
                    onClose={() => setSelected(null)}
                    onPatch={patchSelected}
                />
            )}
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="feedback-detail-row">
            <div className="feedback-detail-key">{label}</div>
            <div className="feedback-detail-val" style={mono ? { fontFamily: 'var(--mono)', fontSize: 12 } : undefined}>{value ?? '—'}</div>
        </div>
    );
}

function FailedAuditDetail({ item, saving, session, onClose, onPatch }) {
    const [notes, setNotes] = useState(item.review_notes || '');
    const [downloading, setDownloading] = useState(false);
    const [dlError, setDlError] = useState(null);

    useEffect(() => { setNotes(item.review_notes || ''); setDlError(null); }, [item.id, item.review_notes]);

    // The bucket is private and no public URL exists. Ask the server to mint a
    // short-lived signed URL at click time, then hand it straight to the browser —
    // never store it, never put it in the DOM. It expires in two minutes.
    const download = async () => {
        setDownloading(true);
        setDlError(null);
        try {
            const res = await fetch(`${API}/api/admin/audit-failures/${item.id}/download-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token || ''}` },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
            window.open(data.url, '_blank', 'noopener,noreferrer');
        } catch (e) { setDlError(e.message); }
        setDownloading(false);
    };

    const expired = !!item.storage_deleted_at;
    const expiresSoon = item.storage_expires_at && !expired &&
        (Date.parse(item.storage_expires_at) - Date.now()) < 7 * 86400000;

    return (
        <div className="gd-drawer-overlay" onClick={onClose}>
            <div className="gd-drawer" onClick={e => e.stopPropagation()}>
                <div className="gd-drawer-head">
                    <div>
                        <h3 style={{ margin: 0 }}>{labelize(item.failure_code) || 'Unknown Failure'}</h3>
                        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
                            {item.file_name || '(no file name)'} · {fmtDate(item.created_at)}
                        </div>
                    </div>
                    <button className="feedback-hbtn" onClick={onClose}>✕</button>
                </div>

                {/* What the customer actually read. First, because every other fact
                    here is in service of answering it. */}
                <div className="feedback-detail-section">
                    <div className="feedback-detail-section-title">What the customer saw</div>
                    <div className="feedback-detail-val" style={{ lineHeight: 1.5 }}>{item.user_message || '—'}</div>
                    {item.manual_review_offered && (
                        <div style={{ marginTop: 8, color: 'var(--amber)', fontSize: 12 }}>
                            This message offered a manual review. Someone owes them an answer.
                        </div>
                    )}
                </div>

                <div className="feedback-detail-section">
                    <div className="feedback-detail-section-title">The file</div>
                    {item.file_available ? (
                        <>
                            <button className="feedback-hbtn" disabled={downloading} onClick={download}>
                                {downloading ? 'Preparing…' : '↓ Download document'}
                            </button>
                            <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>
                                Private bucket; the link is signed and expires in 2 minutes. Kept only to honour the
                                review we offered — this is <strong>not</strong> corpus consent, which is separate and
                                opt-in.
                            </div>
                            {expiresSoon && (
                                <div style={{ color: 'var(--amber)', fontSize: 12, marginTop: 8 }}>
                                    Retention ends {fmtDate(item.storage_expires_at)} — after that the document is deleted.
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.5 }}>
                            {expired
                                ? `Deleted ${fmtDate(item.storage_deleted_at)} at the end of its retention window. The record remains; the document does not.`
                                : item.storage_skip_reason
                                    ? `No file stored — ${item.storage_skip_reason}`
                                    : 'No file stored for this failure. (We only keep the document where the message offered a manual review.)'}
                        </div>
                    )}
                    <Row label="Size" value={fmtBytes(item.stored_size_bytes ?? item.file_size_bytes)} mono />
                    <Row label="Basis" value={item.storage_consent_basis ? labelize(item.storage_consent_basis) : '—'} />
                    <Row label="Expires" value={item.storage_expires_at ? fmtDate(item.storage_expires_at) : '—'} />
                </div>

                <div className="feedback-detail-section">
                    <div className="feedback-detail-section-title">Triage</div>
                    <label className="feedback-field">
                        <span className="feedback-field-label">Review status</span>
                        <select className="feedback-select" value={item.review_status} disabled={saving}
                            onChange={e => onPatch({ review_status: e.target.value })}>
                            {REVIEW_STATUSES.map(s => <option key={s} value={s}>{labelize(s)}</option>)}
                        </select>
                    </label>
                    <label className="feedback-field">
                        <span className="feedback-field-label">Notes</span>
                        <textarea className="feedback-select" rows={3} value={notes} disabled={saving}
                            onChange={e => setNotes(e.target.value)}
                            onBlur={() => { if (notes !== (item.review_notes || '')) onPatch({ review_notes: notes }); }} />
                    </label>
                    <Row label="Reviewed By" value={item.reviewed_by} />
                    <Row label="Reviewed At" value={item.reviewed_at ? fmtDate(item.reviewed_at) : '—'} />
                </div>

                {/* Re-running lives in a script, not a button: it calls the real
                    runAudit and can take minutes, and its result is a conversation
                    with the customer, not an automatic re-delivery. */}
                {item.file_available && (
                    <div className="feedback-detail-section">
                        <div className="feedback-detail-section-title">Re-run this document</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 6, lineHeight: 1.5 }}>
                            Once the format is supported (or an OCR key is set), re-run the stored file through the
                            real audit. Nothing is saved or sent to the customer — it answers “would this work now?”
                        </div>
                        <code className="feedback-detail-val" style={{ fontFamily: 'var(--mono)', fontSize: 11, display: 'block', wordBreak: 'break-all' }}>
                            node backend/reprocess-failed-audit.js {supportRef(item.correlation_id) || item.id} --mark
                        </code>
                    </div>
                )}

                <div className="feedback-detail-section">
                    <div className="feedback-detail-section-title">Diagnosis</div>
                    <Row label="Support Reference" value={supportRef(item.correlation_id) || '—'} mono />
                    <Row label="Failure Code" value={item.failure_code} mono />
                    <Row label="Detected Type" value={item.detected_document_type} />
                    <Row label="Document Hint" value={item.document_hint ? labelize(item.document_hint) : '—'} />
                    <Row label="Source" value={item.source_type || '—'} />
                    <Row label="Pages" value={item.page_count ?? '—'} mono />
                    <Row label="Error" value={item.error_message} mono />
                    <Row label="Build" value={item.commit_sha ? `${item.app_version || ''} ${item.commit_sha.slice(0, 8)}` : '—'} mono />
                    <Row label="Correlation ID" value={item.correlation_id} mono />
                    {item.diagnostics && Object.keys(item.diagnostics).length > 0 && (
                        <Row label="Diagnostics" value={JSON.stringify(item.diagnostics)} mono />
                    )}
                </div>

                <div className="feedback-detail-section">
                    <div className="feedback-detail-section-title">Who</div>
                    <Row label="User" value={item.user_id || 'Anonymous'} mono />
                    <Row label="Account" value={item.account_id || '—'} mono />
                </div>

                {dlError && <div className="feedback-error" style={{ margin: '8px 0' }}>{dlError}</div>}
            </div>
        </div>
    );
}

export default FailedAudits;
